import React from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import { CheckboxInput, LightTooltip, TextDefault } from '@awarego-ui/components';
import BounceReasonCell, {
    bounceReasonSort,
    humanizeEmailType,
} from './BounceReasonCell';
import { caseAndDiacriticsInsensitiveSort } from '../../../components/table/table-helpers';

function listLabel(item) {
    if (typeof item === 'string') return item;
    if (item && typeof item === 'object') return item.title || item.name || '';
    return '';
}

function NameEmailLabel({ row }) {
    const name = row.name;
    const email = row.email;
    const lists = (Array.isArray(row.lists_names) ? row.lists_names : [])
        .map(listLabel)
        .filter(Boolean);
    const label = (
        <span
            style={{
                display: 'inline-flex',
                flexDirection: 'column',
                lineHeight: 1.3,
                maxWidth: 280,
            }}
        >
            <span>{name || email}</span>
            {name && email ? (
                <span
                    style={{
                        fontSize: 12,
                        fontWeight: 400,
                        opacity: 0.7,
                    }}
                >
                    {email}
                </span>
            ) : null}
        </span>
    );

    if (!lists.length) return label;

    return (
        <LightTooltip
            title={lists.map((listName, index) => (
                <div key={`${listName}-${index}`}>{listName}</div>
            ))}
        >
            {label}
        </LightTooltip>
    );
}

function isRowSelected(store, email) {
    if (store.selection.allSelected) {
        return !store.selection.unChecked[email];
    }
    return !!store.selection.checked[email];
}

function formatLastAttempt(value, attempts) {
    if (!value) return '—';
    const m = moment(value);
    const when = m.isValid() ? m.format('ll HH:mm') : String(value);
    if (attempts != null && attempts !== '' && !Number.isNaN(Number(attempts))) {
        return `${when} · ${attempts}×`;
    }
    return when;
}

export function bouncedEmailColumns(store) {
    const rows = (store && store.usersList) || [];
    const showEmailType = rows.some((r) => r && r.emailType);

    const columns = [
        {
            Header: 'Name',
            id: 'name',
            accessor: (x) => {
                return x.name || x.email;
            },
            sortType: caseAndDiacriticsInsensitiveSort,
            Cell: observer((x) => {
                const email = x.row.original.email;
                return (
                    <CheckboxInput
                        label={<NameEmailLabel row={x.row.original} />}
                        checked={isRowSelected(store, email)}
                        noYPadding={true}
                        onChange={(e) => {
                            store.selection.toggleRow(
                                x.row.original,
                                e.target.checked
                            );
                        }}
                    />
                );
            }),
        },
        {
            Header: 'Bounce reason',
            id: 'bounceReason',
            accessor: (row) => {
                const code = row.code || (row.reason && row.reason.code) || '';
                const message =
                    (row.reason && (row.reason.message || row.reason.type)) ||
                    '';
                return `${code} ${message}`.trim();
            },
            sortType: bounceReasonSort,
            Cell: (x) => (
                <BounceReasonCell
                    reason={x.row.original.reason}
                    code={x.row.original.code}
                    bounceKind={x.row.original.bounceKind}
                />
            ),
        },
        {
            Header: 'Last attempt',
            id: 'lastAttemptAt',
            accessor: 'lastAttemptAt',
            Cell: (x) => (
                <TextDefault>
                    {formatLastAttempt(
                        x.value,
                        x.row.original.attempts
                    )}
                </TextDefault>
            ),
        },
    ];

    if (showEmailType) {
        columns.push({
            Header: 'Email type',
            accessor: 'emailType',
            Cell: (x) =>
                x.value ? (
                    <TextDefault>{humanizeEmailType(x.value)}</TextDefault>
                ) : (
                    <TextDefault lighter>—</TextDefault>
                ),
        });
    }

    return columns;
}
